import React, { useEffect, useState } from "react";
import {
  Layout,
  Input,
  Button,
  Select,
  message,
  Space,
  TableColumnsType,
  DatePicker,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import "./index.css";
import HeaderOfList from "../../../../assets/img/headerForList.png";
import {
  PlusOutlined,
  DeleteOutlined,
  CopyOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { get, getBlob, remove } from "../../../../utils/apiUtils";
import { getUserRole } from "../../../../hooks/getUserRoles";
import { normalizeRole } from "../../../../utils/roles";
import DeletedTranslation from "../DeletedTranslations";
import GenericTable from "../../../GeneralTable/GenericTable";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

const { Content } = Layout;
const { Search } = Input;
const { Option } = Select;

interface DataType {
  key: React.Key;
  filename: string;
  type_name: string;
  date: string;
  actions: JSX.Element;
}

const getColumns = (t: TFunction): TableColumnsType<DataType> => [
  {
    title: t("translationList.columns.history"),
    dataIndex: "filename",
    sorter: (a, b) => a.filename.localeCompare(b.filename),
  },
  {
    title: t("translationList.columns.type"),
    dataIndex: "type_name",
    sorter: (a, b) => a.type_name.localeCompare(b.type_name),
  },
  {
    title: t("translationList.columns.date"),
    dataIndex: "date",
    sorter: (a, b) => {
      const dateA = dayjs(a.date, "MM/DD/YYYY");
      const dateB = dayjs(b.date, "MM/DD/YYYY");
      return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
    },
    render: (text) =>
      text === "Invalid date" ? (
        <span style={{ color: "red" }}>{text}</span>
      ) : (
        text
      ),
  },
  {
    title: "",
    dataIndex: "actions",
  },
];

const data: DataType[] = [
  {
    key: "1",
    filename: "Translation 1",
    type_name: "Text",
    date: "05/02/2024",
    actions: (
      <div className="actions">
        <CopyOutlined />
        <DownloadOutlined />
        <DeleteOutlined />
      </div>
    ),
  },
  {
    key: "2",
    filename: "Translation 2",
    type_name: "Json",
    date: "05/02/2024",
    actions: (
      <div className="actions">
        <CopyOutlined />
        <DownloadOutlined />
        <DeleteOutlined />
      </div>
    ),
  },
  {
    key: "3",
    filename: "Translation 3",
    type_name: "File",
    date: "05/02/2024",
    actions: (
      <div className="actions">
        <CopyOutlined />
        <DownloadOutlined />
        <DeleteOutlined />
      </div>
    ),
  },
];

const rowSelection = {
  onChange: (selectedRowKeys: React.Key[], selectedRows: DataType[]) => {},
  getCheckboxProps: (record: DataType) => ({
    disabled: record.filename === "Disabled User",
    name: record.filename,
  }),
};

function AITranslation() {
  const { t } = useTranslation("aiTools");
  const userRole = getUserRole();
  const { id: projectId } = useParams();
  const columns = getColumns(t);

  const [selectionType, setSelectionType] = useState<"checkbox">("checkbox");
  const [searchText, setSearchText] = useState<string>("");
  const [filteredData, setFilteredData] = useState<DataType[]>(data);
  const [typeFilter, setTypeFilter] = useState<string>(""); // 🔹 fillon bosh
  const [dateFilter, setDateFilter] = useState<Dayjs | string>("");
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [translationList, setTranslationList] = useState<any>([]);
  const [translationTypes, setTranslationTypes] = useState([]);
  const [jsonName, setJsonName] = useState("");
  const [textName, setTextName] = useState("");
  const [excelName, setExcelName] = useState("");
  const [loading, setLoading] = useState(false);
  const [isDeletedModalVisible, setIsDeletedModalVisible] = useState(false);

  const navigate = useNavigate();

  const filterData = (): void => {
    const lowercasedSearchText = searchText.toLowerCase();
    const filtered = translationList.filter((item: any) => {
      const searchTextMatch = item.filename
        .toLowerCase()
        .includes(lowercasedSearchText);
      const typeMatch = !typeFilter || item.type_name === typeFilter; // 🔹 pa filtër kur është bosh
      const dateMatch =
        !dateFilter || dayjs(item.date).isSame(dayjs(dateFilter), "day");
      return searchTextMatch && typeMatch && dateMatch;
    });
    setFilteredData(filtered);
  };

  const copyTranslation = async (id: React.Key) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await getBlob<Blob>(
        `copy_translation/${id}?project_id=${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        }
      );

      if (response.type === "success") {
        const text = await response.data.text();
        if (response.headers["content-type"]?.includes("application/json")) {
          const json = JSON.parse(text);
          if (json.content && typeof json.content === "object") {
            await navigator.clipboard.writeText(JSON.stringify(json.content));
          } else {
            await navigator.clipboard.writeText(json.content);
          }
        } else {
          await navigator.clipboard.writeText(text);
        }
        message.success(t("translationList.messages.copySuccess"));
      } else {
        console.error("Error copying the translation:", response);
        message.error(t("translationList.messages.copyError"));
      }
    } catch (error) {
      console.error("Error copying the translation:", error);
      message.error(t("translationList.messages.copyError"));
    } finally {
      setLoading(false);
    }
  };

  const openDeletedTranslationsModal = () => {
    setIsDeletedModalVisible(true);
  };

  const closeDeletedTranslationsModal = () => {
    setIsDeletedModalVisible(false);
  };

  const downloadTranslation = async (
    id: React.Key,
    filename: any,
    type: any
  ) => {
    setLoading(true);
    try {
      const response = await getBlob<Blob>(
        `translations/download/${id}?project_id=${projectId}`,
        {
          responseType: "blob",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.type === "success") {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        const fileExtension =
          type === "excel" ? ".xlsx" : type === "json" ? ".json" : ".txt";
        link.href = url;
        link.setAttribute("download", `${filename}${fileExtension}`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        message.success(t("translationList.messages.downloadSuccess"));
      } else {
        throw new Error(
          "Failed to download file, server responded with error."
        );
      }
    } catch (error) {
      console.error("Failed to download translation:", error);
      message.error(t("translationList.messages.downloadError"));
    } finally {
      setLoading(false);
    }
  };

  const deleteTranslation = async (id: React.Key) => {
    if (!window.confirm(t("translationList.confirmDelete"))) {
      return;
    }
    setLoading(true);
    try {
      const response = await remove(
        `translations/${id}?project_id=${projectId}`,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.type === "success") {
        message.success(t("translationList.messages.deleteSuccess"));
        await fetchTranslations();
      } else {
        throw new Error("Server responded with an error during deletion.");
      }
    } catch (error) {
      console.error("Failed to delete translation:", error);
      message.error(t("translationList.messages.deleteError", { error }));
    } finally {
      setLoading(false);
    }
  };

  const isValidDate = (dateStr: any, format = "MM/DD/YYYY") => {
    return dayjs(dateStr, format).isValid();
  };

  const fetchTranslations = async () => {
    setLoading(true);
    try {
      const response = await get<any>(
        `users/translations?project_id=${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.type === "success") {
        const sortedData = [...response.data].sort(
          (a, b) =>
            dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf()
        );

        const fetchedTranslations = sortedData.map((item: any) => {
          const isValid = isValidDate(item.date);
          return {
            ...item,
            key: item.id,
            date: dayjs(item.created_at).format("MM/DD/YYYY"),
            actions: (
              <Space size="middle" className="actions">
                {item.type_name !== "excel" && (
                  <CopyOutlined onClick={() => copyTranslation(item.id)} />
                )}
                <DownloadOutlined
                  onClick={() =>
                    downloadTranslation(item.id, item.filename, item.type_name)
                  }
                />
                <DeleteOutlined onClick={() => deleteTranslation(item.id)} />
              </Space>
            ),
          };
        });

        setTranslationList(fetchedTranslations);
        setFilteredData(fetchedTranslations);
      } else {
        message.error(
          t("translationList.messages.fetchError", {
            message: response.data.message,
          })
        );
      }
    } catch (error) {
      message.error(t("translationList.messages.fetchUnexpectedError"));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTranslations();
  }, [projectId]);

  const fetchTranslationTypes = async () => {
    setLoading(true);
    try {
      const response = await get<any>("translation-types", {
        headers: {
          Accept: "application/json",
        },
      });

      if (response.type === "success") {
        setTranslationTypes(response.data);
      } else {
        throw new Error(
          "Failed to fetch translation types, server responded with error."
        );
      }
    } catch (error: any) {
      console.error("Failed to fetch translation types:", error);
      message.error(
        t("translationList.messages.fetchTypesError", {
          message: error.message,
        })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTranslationTypes();
  }, []);

  const handleTypeChange = (value: any) => {
    setTypeFilter(value || ""); // 🔹 nëse pastrohet, bëhet bosh
    filterData();
  };

  const handleDateChange = (date: Dayjs | null, dateString: string) => {
    setSelectedDate(date);
    setDateFilter(date ? date.format("YYYY-MM-DD") : "");
    filterData();
  };

  const handleReset = () => {
    setTypeFilter(""); // 🔹 pa vlerë
    setDateFilter("");
    setSearchText("");
    filterData();
  };

  useEffect(() => {
    filterData();
  }, [searchText, typeFilter, dateFilter, translationList]);

  const onSearch = (value: string) => {
    setSearchText(value);
  };

  const handleRedirect = () => {
    navigate(`/add-ai-translation/${projectId}`);
  };

  return (
    <>
      <div style={{ marginLeft: 0 }} className="ai-translationList-layout">
        <Content
          className="content-styles"
          style={{
            padding: "0 24",
            margin: 0,
            minHeight: 280,
          }}
        >
          <div className="header-image-for-ai-translation">
            <img
              src={HeaderOfList}
              alt="AI Tool Header"
              style={{ width: "100%" }}
            />
          </div>
          <div className="translation-tool-content">
            <div className="title-button">
              <h2>{t("translationList.title")}</h2>
              <div className="reset-filters-button">
                {normalizeRole(userRole) !== "viewer" && (
                  <>
                    <Button
                      className="button-stylee-forReset"
                      onClick={handleReset}
                    >
                      <DeleteOutlined /> {t("translationList.resetFilters")}
                    </Button>
                    <Button
                      className="button-stylee-deleted"
                      onClick={openDeletedTranslationsModal}
                      style={{ width: "100%" }}
                    >
                      <DeleteOutlined />{" "}
                      {t("translationList.deletedTranslations")}
                    </Button>

                    <Button className="button-stylee" onClick={handleRedirect}>
                      <PlusOutlined /> {t("translationList.newTranslation")}
                    </Button>
                  </>
                )}
              </div>{" "}
            </div>
            <div className="filters-style">
              <Search
                placeholder={t("translationList.searchPlaceholder")}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onSearch={onSearch}
                style={{ width: "100%" }}
                enterButton
              />

              <Select
                placeholder={t("translationList.typePlaceholder")}
                style={{ width: "100%" }}
                onChange={handleTypeChange}
                value={typeFilter || undefined} // 🔹 nëse bosh, s’është e selektuar asgjë
                allowClear
              >
                {translationTypes.map((type: any) => (
                  <Option key={type.id} value={type.type_name}>
                    {type.type_name}
                  </Option>
                ))}
              </Select>

              <DatePicker
                onChange={(date, dateString) =>
                  handleDateChange(date, dateString)
                }
                style={{ width: "100%" }}
                format="MM/DD/YYYY"
              />
            </div>
            <div>
              <GenericTable
                rowSelection={{
                  type: selectionType,
                  ...rowSelection,
                }}
                className="ai-tools"
                columns={columns}
                dataSource={filteredData}
                loading={loading}
                emptyText={t("translationList.emptyText")}
                onRowClick={(record) => console.log("Row clicked:", record)}
              />
            </div>
          </div>
        </Content>

        <DeletedTranslation
          visible={isDeletedModalVisible}
          onCancel={closeDeletedTranslationsModal}
          projectId={projectId}
          restore={fetchTranslations}
        />
      </div>
    </>
  );
}

export default AITranslation;
